import React, { useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpRight, ArrowRight, ArrowDownRight } from 'lucide-react';
import { OperatorData } from '@/types/features/operator-proficiency/operator-proficiency.types';
import { getAbuseColor } from '@/modules/AssetsPreviewPage/OperatorProficiency/utils';
import { BaseTooltip } from '@/components/uiKit/Tooltip';
import { Highlight } from '@/components/uiKit/Highlight';

export const useOperatorProficiencyTable = () => {
  const [sortingState, setSortingState] = useState([{ id: 'eventsCount', desc: true }]);

  const columns: ColumnDef<OperatorData>[] = useMemo(
    () => [
      // rank - from API data
      {
        id: 'rank',
        header: () => <div className="whitespace-nowrap">Rank</div>,
        accessorKey: 'rank',
        size: 40,
        enableSorting: true,
        cell: ({ row }) => {
          const rank = (row.original as any).rank ?? row.index + 1;
          return <div className="font-semibold text-base text-gray-900">{rank}</div>;
        },
      },
      // driver - composite type with id and name
      {
        id: 'driver',
        header: () => <div className="whitespace-nowrap">Drivers Performance</div>,
        accessorKey: 'name',
        accessorFn: (row: any) => `${row.id || row.driverId || ''} ${row.name || row.driverName || row.operatorName || ''}`,
        size: 200,
        minSize: 200,
        maxSize: 250,
        enableSearch: true,
        enableGlobalSearch: true,
        filterFn: (row, _id, value) => {
          const searchValue = value.toLowerCase();
          const original = row.original as any;
          const id = original.id?.toLowerCase() || '';
          const driverId = original.driverId?.toLowerCase() || '';
          const name = original.name?.toLowerCase() || '';
          const driverName = original.driverName?.toLowerCase() || '';
          const operatorName = original.operatorName?.toLowerCase() || '';
          return id.includes(searchValue) || driverId.includes(searchValue) || 
                 name.includes(searchValue) || driverName.includes(searchValue) || 
                 operatorName.includes(searchValue);
        },
        cell: ({ row, table }) => {
          const searchValue = (table.options.meta as any)?.searchValue || '';
          const original = row.original as any;
          const driverId = original.id || original.driverId || '';
          const driverName = original.name || original.driverName || original.operatorName || '';

          return (
            <div className="min-w-0">
              {driverId && (
                <div className="font-semibold text-sm text-gray-900 truncate">
                  <Highlight text={driverId} searchTerm={searchValue} />
                </div>
              )}
              <BaseTooltip content={driverName} side="top">
                <div className="text-gray-500 text-sm truncate">
                  <Highlight text={driverName} searchTerm={searchValue} />
                </div>
              </BaseTooltip>
            </div>
          );
        },
      },
      // eventsCount - number type
      {
        id: 'eventsCount',
        header: () => <div className="whitespace-nowrap">Events Count</div>,
        accessorKey: 'eventsCount',
        accessorFn: (row: any) => row.eventsCount ?? row.count ?? row.eventsDuration ?? 0,
        size: 90,
        minSize: 90,
        maxSize: 90,
        enableSorting: true,
        cell: ({ row }) => {
          const original = row.original as any;
          const count = original.eventsCount ?? original.count ?? original.eventsDuration;
          if (count === undefined || count === null) return null;
          return (
            <span className="font-bold text-sm text-gray-900">{count}</span>
          );
        },
      },
      // trend - trend type
      {
        id: 'trend',
        header: () => <div className="whitespace-nowrap">Trend</div>,
        accessorKey: 'trend',
        accessorFn: (row: any) => row.trend ?? row.eventsTrend ?? 0,
        size: 60,
        minSize: 60,
        maxSize: 60,
        enableSorting: true,
        cell: ({ row }) => {
          const original = row.original as any;
          const trend = original.trend ?? original.eventsTrend ?? 0;

          // Negative trends are good (green), positive trends are bad (red)
          const trendColor = trend < 0 ? 'text-emerald-600' : trend > 0 ? 'text-rose-600' : 'text-gray-500';
          const trendDisplay = trend !== 0 ? `${Math.abs(trend)}%` : '0%';
          const sign = trend < 0 ? '-' : trend > 0 ? '+' : '';

          return (
            <div className="flex items-center gap-1">
              {trend < 0 && <ArrowDownRight className={`w-3 h-3 ${trendColor}`} strokeWidth={3} />}
              {trend > 0 && <ArrowUpRight className={`w-3 h-3 ${trendColor}`} strokeWidth={3} />}
              {trend === 0 && <ArrowRight className={`w-3 h-3 ${trendColor}`} strokeWidth={3} />}
              <span className={`text-xs font-bold ${trendColor}`}>
                {sign}
                {trendDisplay}
              </span>
            </div>
          );
        },
      },
      // topAbuses - badges type with maxItems: 3
      {
        id: 'topAbuses',
        header: () => <div className="whitespace-nowrap">Top Event Types</div>,
        accessorKey: 'topAbuses',
        accessorFn: (row: any) => {
          // Handle both array format (new) and object format (legacy)
          const abuses = row.topAbuses || row.topEvents;
          if (Array.isArray(abuses)) {
            return abuses.map((a: any) => a.name || a).join(' ');
          }
          // Legacy object format
          const eventsList = [];
          if (abuses?.first?.name) eventsList.push(abuses.first.name);
          if (abuses?.second?.name) eventsList.push(abuses.second.name);
          if (abuses?.third?.name) eventsList.push(abuses.third.name);
          return eventsList.join(' ');
        },
        size: 600,
        minSize: 400,
        enableSorting: false,
        enableSearch: false,
        enableGlobalSearch: false,
        cell: ({ row }) => {
          const original = row.original as any;
          const abuses = original.topAbuses || original.topEvents;

          // Handle array format (new API)
          if (Array.isArray(abuses)) {
            // Filter out empty/null names and take first 3
            const badges = abuses
              .slice(0, 3)
              .map((abuse: any) => typeof abuse === 'string' ? abuse : abuse?.name)
              .filter((name: string) => name && name.trim());
            if (badges.length === 0) return null;

            return (
              <div className="grid grid-cols-3 gap-2 w-full">
                {badges.map((name: string, index: number) => {
                  const bgColor = getAbuseColor(name);
                  return (
                    <div key={index} className="flex items-center gap-1.5 min-w-0">
                      <div className={`w-1 h-2.5 flex-shrink-0 ${bgColor}`}></div>
                      <BaseTooltip content={name} side="top">
                        <div className="text-sm text-gray-900 truncate">{name}</div>
                      </BaseTooltip>
                    </div>
                  );
                })}
              </div>
            );
          }

          // Legacy object format (first, second, third)
          const events = [abuses?.first, abuses?.second, abuses?.third]
            .filter((event: any) => event && event.name && event.name.trim());
          if (events.length === 0) return null;

          return (
            <div className="grid grid-cols-3 gap-2 w-full">
              {events.map((event: any, index: number) => {
                const bgColor = getAbuseColor(event.name);
                return (
                  <div key={index} className="flex items-center gap-1.5 min-w-0">
                    <div className={`w-1 h-2.5 flex-shrink-0 ${bgColor}`}></div>
                    <BaseTooltip content={event.name} side="top">
                      <div className="text-sm text-gray-900 truncate">{event.name}</div>
                    </BaseTooltip>
                  </div>
                );
              })}
            </div>
          );
        },
      },
    ],
    []
  );

  return {
    tableStructure: columns,
    sortingState,
    onSortingChange: setSortingState,
  };
};
